'use client'
import { useGetCompanySearchQuery } from '@/app/globalRedux/api/searchSlice'
import { setSearchInitialLoad } from '@/app/globalRedux/dataSlice'
import {
  setCompanyOrder,
  setCompanySector,
  setCompanySelectedOption,
  setFvSelectedActivity,
} from '@/app/globalRedux/filter/companyFilter'
import {
  setProductFvActivity,
  setProductSector,
} from '@/app/globalRedux/filter/productFilter'
import {
  setCountFvListedRegion,
  setCountPphListedRegion,
  setCountTlListedRegion,
  setFvListedProductFamilyCount,
  setListedBuyerSupplier,
} from '@/app/globalRedux/filterList/companyFilterList'
import { RootState } from '@/app/globalRedux/store'
import { useTranslation } from '@/app/i18n/client'
import { CompanyResponse } from '@/app/types/companySearchType'
import SearchResultSort from '@/components/SearchResultSelect'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Skeleton } from '@/components/ui/skeleton'
import { closeModal } from '@/lib/filterMobileModalHandler'
import { convertNaNToOne } from '@/lib/formatUtils'
import CloseIcon from '@mui/icons-material/Close'
import { useParams } from 'next/navigation'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ClearAllFilter from '../CommonFilters/ClearAllFilter'
import CompanyFilterSections from './CompanyFilterSections'
import { usePageNumberUpdator } from '@/app/hooks/usePageParamUpdater'

const CompanyFilter = ({ search }: { search: string }) => {
  const { t } = useTranslation()
  const params = useParams()
  const dispatch = useDispatch()
  const goToPage = usePageNumberUpdator()
  const { order: companyOrder, companySelectedSortOption } = useSelector(
    (state: RootState) => state.companyFilter
  )
  const [isMounted, setIsMounted] = useState(false)
  const commonFilter = useSelector((state: RootState) => state.commonFilter)
  const productFilter = useSelector((state: RootState) => state.productFilter)
  const companyFilter = useSelector((state: RootState) => state.companyFilter)
  const sectors = [
    {
      id: 'AF Production',
      value: 'Fruit & Vegetable',
    },
    {
      id: 'AF Pre & Post Harvest',
      value: 'Pre - Post Harvest',
    },
    {
      id: 'AF Transports',
      value: 'Transport / Logistics',
    },
    {
      id: 'AF Retail Chains',
      value: 'Retail Chains',
    },
  ]
  const { data: companyData, isFetching: isCompanyDataFetching } =
    useGetCompanySearchQuery<{
      data: CompanyResponse
      isFetching: boolean
    }>(
      {
        search_on: search,
        companyFilter: companyFilter,
        commonFilter: commonFilter,
        productFilter: productFilter,
        lang: String(params?.lng),
        pageNo: convertNaNToOne(Number(params?.companyPageNo?.[0] || 1)),
      },
      {
        skip: companyFilter.isRouteUpdating,
      }
    )
  useEffect(() => {
    if (companyData && !isCompanyDataFetching) {
      dispatch(
        setListedBuyerSupplier(
          companyData?.facet_count?.activites_english ?? []
        )
      )
      dispatch(setCountPphListedRegion(companyData?.facet_count?.region ?? []))
      dispatch(setCountTlListedRegion(companyData?.facet_count?.region ?? []))

      dispatch(setCountFvListedRegion(companyData?.facet_count?.region ?? []))
      dispatch(
        setFvListedProductFamilyCount(
          companyData?.facet_count?.product_family ?? []
        )
      )
      if (companyData?.selected_industry) {
        const sector = sectors.find(
          (item) => item?.id === companyData?.selected_industry
        )
        dispatch(setSearchInitialLoad(false))
        if (companyFilter.sector !== sector?.value) {
          dispatch(setProductSector(sector?.value))
          dispatch(setCompanySector(sector ? sector?.value : ''))
        }
      }
    }
  }, [isCompanyDataFetching, companyData])

  const handleSectorCount = (sector_name: string) => {
    if (companyData && companyData?.facet_count?.sector?.length > 0) {
      const sector = sectors?.find((item) => item?.value === sector_name)
      const count = companyData?.facet_count?.sector?.find(
        (item) => item?.[sector ? sector?.id : ''] !== undefined
      )
      return count?.[sector ? sector?.id : ''] || 0
    }
    return 0
  }

  const hasActivityFilters =
    companyFilter.fvSelectedActivity?.length > 0 ||
    productFilter.productFruitVegetableActivity?.length > 0

  const clearActivityFilters = () => {
    dispatch(setFvSelectedActivity([]))
    dispatch(setProductFvActivity([]))
  }

  const handleOnClick = (value: string) => {
    const applySectorChanges = () => {
      dispatch(setCompanySector(value))
      dispatch(setProductSector(value))
      goToPage({ newPageNo: 1 })
    }
    if (
      hasActivityFilters &&
      confirm(t('Applied Activities Filter will be removed')) == true
    ) {
      clearActivityFilters()
      applySectorChanges()
      return
    } else if (!hasActivityFilters) {
      applySectorChanges()
      return
    }
  }

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!isMounted) {
    return <Skeleton className="h-[300px]  w-[300px] rounded-sm" />
  }

  return (
    <div className="w-[299px] mobile:w-[360px]" suppressHydrationWarning>
      <div>
        <div className="text-[16px] p-2 shadow-[0_0_3px_0_rgba(0,0,0,0.06)] bg-neutral-50 border-2 border-solid border-[#ebebeb] w-full flex">
          <ClearAllFilter />
          <span className="hidden mobile:block mobile:ms-2">
            <CloseIcon
              onClick={() => {
                closeModal('company-modal')
              }}
            />
          </span>
        </div>
        <span className="hidden mobile:block">
          <SearchResultSort
            options="companySort"
            selectedOption={companySelectedSortOption}
            sortByFunction={(value) =>
              dispatch(setCompanySelectedOption(value))
            }
            order={companyOrder}
            orderByFunction={(value) => dispatch(setCompanyOrder(value))}
          />
        </span>
      </div>
      <div className="shadow-[0_0_3px_0_rgba(0,0,0,0.06)] bg-neutral-50 border border-solid border-[#ebebeb] mt-[20px] rounded-[4px] mobile:mt-[10px]">
        <div>
          <Accordion type="single" collapsible defaultValue="item-1">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-[18px] font-semibold text-[#222]">
                {t('Sector')}
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col">
                  <RadioGroup
                    defaultValue="Fruit & Vegetable"
                    className="flex gap-4 items"
                    id="product_type"
                    onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                      const value = (e.target as HTMLInputElement).value
                      if (value) {
                        handleOnClick(value)
                      }
                    }}
                  >
                    <div className="flex flex-col gap-2 checked-color">
                      {sectors.map((sector) => (
                        <Label
                          key={sector.value}
                          className="cursor-pointer flex gap-2 text-sm text-left text-[#6f6f6f]"
                        >
                          <RadioGroupItem
                            value={sector.value}
                            className="!text-[#df7d2f]"
                            checked={sector.value === companyFilter.sector}
                          />
                          {`${t(sector.value)} (${handleSectorCount(sector.value)})`}
                        </Label>
                      ))}
                    </div>
                  </RadioGroup>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
      <CompanyFilterSections />
    </div>
  )
}

export default CompanyFilter
